import classNames from 'classnames';
import { differenceInYears, format } from 'date-fns';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Team } from 'modules/teams/domain/interfaces/team';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { deleteTournamentPlayerApplication } from 'modules/teams/store/players/actions';
import { playersIsLoadingSelector } from 'modules/teams/store/players/selectors';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { AppDispatch } from 'store/store';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { DivProps } from '../../../../../../../typings';
import { TeamPlayerAvatar } from '../../../team-card/styles';
import { PlayerCardHeaderRow } from './styles';

type PlayerCardHeaderProps = {
  teamId: number;
  player: TeamPlayer;
  containerProps?: Omit<DivProps, 'children'>;
  index: number;
};

const PlayerCardHeader = (props: PlayerCardHeaderProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const open = useCallback(() => setIsModalOpen(true), []);
  const close = useCallback(() => setIsModalOpen(false), []);
  const isLoading = useSelector(playersIsLoadingSelector);
  const queryClient = useQueryClient();

  const { teamId, player, containerProps, index } = props;

  const name = [player.lastName, player.firstName, player.middleName].filter(Boolean).join(' ');
  const birthDate = player.birthDate ? new Date(player.birthDate) : null;
  const birthDateString = birthDate ? format(birthDate, 'dd.MM.yyyy') : '';
  const years = birthDate ? differenceInYears(new Date(), birthDate) : null;
  const yearsString = years ? t('ages', { count: years }) : '';
  const fullBirthDate = [birthDateString, yearsString].filter(Boolean).join(', ') || '-';

  const handleKick = useCallback(async () => {
    try {
      await dispatch(deleteTournamentPlayerApplication({ userId: player.userId, teamId })).unwrap();

      queryClient.setQueryData<Team>(['team', teamId], (old) => {
        if (!old) return old as any;
        return {
          ...old,
          players: old.players.filter((p) => p.userId !== player.userId),
        };
      });

      toast.success('Игрок успешно исключен');
      close();
    } catch (e) {
      toast.error('Не удалось исключить игрока');
    }
  }, [dispatch, player.userId, teamId, queryClient, close]);

  const handleClickEdit = useCallback(() => {
    navigate(Pages.PLAYERS.builders.view(player.userId), { state: { fromTeam: teamId } });
  }, [navigate, player.userId, teamId]);

  return (
    <PlayerCardHeaderRow {...containerProps} className={classNames(containerProps?.className)} onClick={() => {}}>
      <div className="player-card">
        <div className="row">
          {index !== undefined ? (
            <UDText type="subhead" className="player-column bold w-1/12">
              {index + 1}
            </UDText>
          ) : (
            ''
          )}

          <div className="player-column w-1/3">
            <TeamPlayerAvatar url={player?.avatar ?? USER_DEFAULT_AVATAR} className="player-avatar" size={40} square />
            <UDText type="subhead" className="bold">
              {name}
            </UDText>
          </div>

          <UDText type="subhead" className="player-column w-1/6" style={{ flexBasis: '15%' }}>
            {fullBirthDate}
          </UDText>

          <div className="player-column flex w-5/12 justify-end">
            <UDButton
              className="mr-3 flex items-center"
              variant="primary"
              onClick={() => {
                handleClickEdit();
              }}
            >
              <span>Редактировать</span>
            </UDButton>

            <UDButton className="flex items-center" variant="secondary" onClick={open}>
              <span>Исключить</span>
            </UDButton>
          </div>
        </div>
      </div>

      <Modal show={isModalOpen} onClose={close}>
        <Modal.Body>
          <UDText type="headline" className="text-surfaceRomanSilver30 px-10" style={{ textAlign: 'center' }}>
            Вы действительно хотите исключить игрока
          </UDText>
          <UDText type="headline" className="text-surfaceRomanSilver30 mb-15 px-10" style={{ textAlign: 'center' }}>
            {player.firstName} {player.lastName} {player.middleName ?? ''}?
          </UDText>
        </Modal.Body>
        <Modal.Footer>
          <UDButton
            disabled={isLoading}
            className="mb-3 flex items-center justify-center"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            stretch
          >
            <span>Нет</span>
          </UDButton>
          <UDButton
            disabled={isLoading}
            loading={isLoading}
            className="mb-3 flex items-center justify-center"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleKick();
            }}
            stretch
          >
            <span>Да</span>
          </UDButton>
        </Modal.Footer>
      </Modal>
    </PlayerCardHeaderRow>
  );
};

export default PlayerCardHeader;
